export const M8_MODULE_REVIEW = {
  id: "m8-module-review",
  title: "Module 8: Complete Review",
  sets: [

    // SET 1: Planning a Backpacking Trip
    {
      id: "m8-review-s1",
      title: "Review 1: Backpacking Adventure",
      category: "review",
      questions: [
        {
          type: "passage",
          passage: `**Planning Our South America Trip**\n\nMy friend Alex and I have been planning our backpacking trip for six months. We've already booked flights to Peru and we've saved enough money for three months of travel. I've never been to South America before, but Alex has visited Brazil twice.`
        },
        {
          type: "match",
          q: "Match the tenses to their uses:",
          pairs: [
            {left: "have been planning", right: "ongoing activity (PPC)"},
            {left: "have booked", right: "completed action (PP)"},
            {left: "have visited", right: "past experience (PP)"},
            {left: "have never been", right: "life experience (PP)"}
          ],
          a: "completed",
          tip: "Present perfect continuous vs present perfect simple uses"
        },
        {
          type: "input",
          q: `We've ___ booked flights. (modifier showing completion earlier than expected)`,
          a: "already",
          tip: "Already = completed, possibly sooner than expected"
        },
        {
          type: "passage",
          passage: `**Current Preparations**\n\nRight now, Alex is learning Spanish for the trip. He's been taking lessons since January. I still haven't bought a new backpack yet, but I'm going shopping this weekend. We're definitely going to visit Machu Picchu - that's certain!`
        },
        {
          type: "mc",
          q: "How long has Alex been taking lessons?",
          options: ["Since January", "For six months", "For one week"],
          a: "Since January",
          tip: "Text says 'since January'"
        },
        {
          type: "order",
          q: "Ongoing situation: 'I ___ haven't bought a backpack ___'",
          words: ["still", "yet"],
          a: "'I still haven't bought a backpack yet'",
          tip: "Still + yet = expected action not done"
        },
        {
          type: "input",
          q: `We're ___ going to visit Machu Picchu. (100% certain plan)`,
          a: "definitely",
          tip: "Definitely = 100% certain intention"
        },
        {
          type: "passage",
          passage: `**Weather Concerns**\n\nWe've been checking weather forecasts. The forecast says it's going to rain in Lima when we arrive. It might be cold in the mountains, so we're packing warm clothes. I think it'll probably be sunny in most places though.`
        },
        {
          type: "mc",
          q: "'It's going to rain' because:",
          options: ["Forecast shows it - evidence", "Random guess", "Just hoping"],
          a: "Forecast shows it - evidence",
          tip: "Going to = prediction based on evidence"
        },
        {
          type: "match",
          q: "Match certainty levels:",
          pairs: [
            {left: "definitely going to", right: "100% certain"},
            {left: "probably will", right: "75% likely"},
            {left: "might be", right: "50% possible"},
            {left: "is going to", right: "evidence-based"}
          ],
          a: "completed",
          tip: "Different certainty and evidence levels"
        },
        {
          type: "passage",
          passage: `**Activities We'll Do**\n\nWe want to try traditional food like ceviche. I enjoy eating spicy dishes, but Alex can't stand anything too hot. We're going to use our cameras for taking photos of the landscapes. We hope to learn about local culture ourselves by staying with families.`
        },
        {
          type: "order",
          q: "Verb patterns: 'I enjoy ___ spicy food' vs 'We want ___ try ceviche'",
          words: ["eating", "to"],
          a: "'enjoy eating' vs 'want to try'",
          tip: "Enjoy + gerund, want + infinitive"
        },
        {
          type: "input",
          q: `We'll learn ___ by staying with families. (reflexive - we'll learn alone)`,
          a: "ourselves",
          tip: "Ourselves = without teacher, independently"
        },
        {
          type: "passage",
          passage: `**Possible Problems**\n\nTravel can be unpredictable! We might have lost luggage - it's possible with connecting flights. It might get uncomfortable sleeping in hostels. Some roads are probably going to be impassable during rainy season. But we know we'll definitely have an amazing experience!`
        },
        {
          type: "mc",
          q: "'Impassable' means:",
          options: ["Not passable (negative prefix)", "Very passable", "Easy to pass"],
          a: "Not passable (negative prefix)",
          tip: "Im- makes 'passable' negative"
        },
        {
          type: "input",
          q: `'It might get ___' (uncomfortable is adjective starting with 'un-')`,
          a: "uncomfortable",
          tip: "Un- + comfortable = not comfortable"
        }
      ]
    },

    // SET 2: Opening a Coffee Shop
    {
      id: "m8-review-s2",
      title: "Review 2: New Business Venture",
      category: "review",
      questions: [
        {
          type: "passage",
          passage: `**My Coffee Shop Dream**\n\nI've always wanted to own a coffee shop. Last year, I finally decided to do it myself! I've been working on the business plan since March. I've already found the perfect location downtown and I've just signed the lease yesterday.`
        },
        {
          type: "match",
          q: "Match the modifiers:",
          pairs: [
            {left: "already", right: "completed action"},
            {left: "just", right: "very recent action"},
            {left: "still", right: "ongoing situation"},
            {left: "yet", right: "expected but not done"}
          ],
          a: "completed",
          tip: "Modifying adverbs with present perfect"
        },
        {
          type: "input",
          q: `I decided to do it ___. (reflexive - without partners)`,
          a: "myself",
          tip: "Myself = alone, independently"
        },
        {
          type: "passage",
          passage: `**Renovation Progress**\n\nThe space needs work. I've been painting the walls for three days and I'm exhausted! I've painted the main area, so that's finished. But I still haven't fixed the broken tiles yet. It's been incredibly tiring!`
        },
        {
          type: "mc",
          q: "Difference between 'I've been painting' and 'I've painted':",
          options: ["Been painting = ongoing, painted = finished", "Both mean the same", "Been painting = finished"],
          a: "Been painting = ongoing, painted = finished",
          tip: "PPC = ongoing activity, PP simple = completed result"
        },
        {
          type: "order",
          q: "Duration: 'I've been painting ___ three days'",
          words: ["for"],
          a: "'for three days'",
          tip: "For + period of time"
        },
        {
          type: "passage",
          passage: `**Menu Planning**\n\nI'm using recipes for making all drinks fresh. I want to serve high-quality coffee and homemade cakes. The ingredients need to be absolutely fresh - nothing frozen or unhealthy. I enjoy experimenting with new flavors myself. I can't stand serving the boring standard menu!`
        },
        {
          type: "input",
          q: `I'm using recipes ___ making fresh drinks. (purpose)`,
          a: "for",
          tip: "For + gerund shows purpose"
        },
        {
          type: "match",
          q: "Match verb patterns:",
          pairs: [
            {left: "want to serve", right: "infinitive"},
            {left: "enjoy experimenting", right: "gerund"},
            {left: "can't stand serving", right: "gerund"},
            {left: "use for making", right: "gerund after 'for'"}
          ],
          a: "completed",
          tip: "Different verbs take different patterns"
        },
        {
          type: "passage",
          passage: `**Hiring Staff**\n\nI've been interviewing baristas all week. How long have you been working in coffee shops? I ask candidates. I'm definitely going to hire someone with experience. I'll probably need three staff members. I might hire my friend Tom, but I haven't decided yet.`
        },
        {
          type: "mc",
          q: "'How long have you been working' asks about:",
          options: ["Duration of ongoing past activity", "Finished past action", "Future plan"],
          a: "Duration of ongoing past activity",
          tip: "How long + PPC = duration question"
        },
        {
          type: "order",
          q: "Certainty levels: '___ going to hire experienced' (100%) vs '___ hire Tom' (50%)",
          words: ["definitely", "might"],
          a: "'definitely going to' vs 'might hire'",
          tip: "Definitely = certain, might = possible"
        },
        {
          type: "passage",
          passage: `**Opening Day Plans**\n\nThe grand opening is next Saturday! I'm meeting the suppliers Friday morning to get the fresh ingredients. I'm having a party Saturday evening to celebrate. I'll make coffee for everyone who comes. I promise I'll definitely be ready! It will probably be the best coffee shop in town!`
        },
        {
          type: "input",
          q: `I'm ___ suppliers Friday. (present continuous for arranged future)`,
          a: "meeting",
          tip: "Present continuous for scheduled future arrangements"
        },
        {
          type: "mc",
          q: "'I'll make coffee for everyone' is:",
          options: ["Spontaneous offer/promise", "Planned decision from yesterday", "Past action"],
          a: "Spontaneous offer/promise",
          tip: "Will for promises and offers"
        },
        {
          type: "passage",
          passage: `**Concerns and Hope**\n\nI'm a bit nervous. It's impossible to predict if customers will come. Some worry is unavoidable, I suppose. But people have been very supportive so far. I think I'll succeed because I'm offering something different. This journey has taught me to believe in myself!`
        },
        {
          type: "order",
          q: "Negative prefixes: '___ to predict' and 'worry is ___'",
          words: ["impossible", "unavoidable"],
          a: "'impossible' and 'unavoidable'",
          tip: "Im- and un- make words negative"
        },
        {
          type: "input",
          q: `Believe in ___. (reflexive - believe in own abilities)`,
          a: "myself",
          tip: "Myself = one's own self"
        }
      ]
    },

    // SET 3: Marathon Training Journey
    {
      id: "m8-review-s3",
      title: "Review 3: Running a Marathon",
      category: "review",
      questions: [
        {
          type: "passage",
          passage: `**Starting My Training**\n\nI've decided to run a marathon! I've never done anything like this before. I've been training every day since the beginning of January. I've already run over 200 miles total. I still haven't run more than 15 miles in one day yet, but that will come!`
        },
        {
          type: "match",
          q: "Match present perfect uses:",
          pairs: [
            {left: "have decided", right: "news/announcement"},
            {left: "have never done", right: "life experience"},
            {left: "have been training", right: "ongoing activity"},
            {left: "have run 200 miles", right: "achievement/result"}
          ],
          a: "completed",
          tip: "Different uses of present perfect tenses"
        },
        {
          type: "input",
          q: `I've ___ run 200 miles. (modifier showing completion)`,
          a: "already",
          tip: "Already = completed, possibly sooner than expected"
        },
        {
          type: "passage",
          passage: `**Weekly Routine**\n\nEvery morning, I wake up at 6am and I go running in the park. I use running shoes for protecting my feet from injuries. After running, I make myself a protein smoothie. I enjoy exercising early because the weather is usually mild and pleasant.`
        },
        {
          type: "mc",
          q: "'I use running shoes for protecting' shows:",
          options: ["Purpose with for + gerund", "Past tense", "Future plan"],
          a: "Purpose with for + gerund",
          tip: "For + -ing shows purpose"
        },
        {
          type: "order",
          q: "Reflexive: 'I make ___ a smoothie' (I make it for myself)",
          words: ["myself"],
          a: "'I make myself a smoothie'",
          tip: "Myself = I do the action to/for myself"
        },
        {
          type: "passage",
          passage: `**Diet Changes**\n\nI've been eating healthier food for two months now. I want to avoid unhealthy snacks and I can't stand eating junk food anymore. I need to eat fresh vegetables and lean protein. I've learned to prepare meals myself. It's been challenging but rewarding!`
        },
        {
          type: "match",
          q: "Match verb patterns:",
          pairs: [
            {left: "want to avoid", right: "infinitive after want"},
            {left: "can't stand eating", right: "gerund after can't stand"},
            {left: "need to eat", right: "infinitive after need"},
            {left: "learned to prepare", right: "infinitive after learn"}
          ],
          a: "completed",
          tip: "Different verbs require different patterns"
        },
        {
          type: "input",
          q: `Food that's not healthy is ___. (negative prefix un-)`,
          a: "unhealthy",
          tip: "Un- + healthy = not healthy"
        },
        {
          type: "passage",
          passage: `**Race Preparation**\n\nThe marathon is in three weeks! I'm meeting my running coach tomorrow morning to discuss strategy. I'm definitely going to finish the race - I've worked too hard to quit! I'll probably complete it in under 4 hours. I might even get a medal if I'm lucky!`
        },
        {
          type: "mc",
          q: "'I'm meeting my coach tomorrow' uses:",
          options: ["Present continuous for arranged future", "Present simple", "Past tense"],
          a: "Present continuous for arranged future",
          tip: "Present continuous for definite arrangements"
        },
        {
          type: "order",
          q: "Certainty levels: '___ going to finish' (100%), '___ complete in 4h' (75%), '___ get medal' (50%)",
          words: ["definitely", "probably", "might"],
          a: "'definitely', 'probably', 'might'",
          tip: "Different certainty levels: 100%, 75%, 50%"
        },
        {
          type: "passage",
          passage: `**Race Day Thoughts**\n\nI'm nervous but excited! The weather forecast says it's going to be sunny and cool - perfect conditions! I think I'll do well because I've prepared thoroughly. If I feel tired, I'll just remind myself why I started. I promise myself I won't give up no matter what!`
        },
        {
          type: "input",
          q: `'It's ___ to be sunny' (forecast shows evidence)`,
          a: "going",
          tip: "Going to for prediction with evidence"
        },
        {
          type: "mc",
          q: "'I'll remind myself' is:",
          options: ["Will for decision in that moment", "Past action", "Planned yesterday"],
          a: "Will for decision in that moment",
          tip: "Will for future conditional decision"
        },
        {
          type: "passage",
          passage: `**After the Race**\n\nI've just completed my first marathon! I've been running for 3 hours and 47 minutes. I'm absolutely exhausted but incredibly happy. I've achieved something I thought was impossible six months ago. How long have you been celebrating? my friend asks. All day! I reply. This experience has changed me completely!`
        },
        {
          type: "order",
          q: "Recent completion: 'I've ___ completed my first marathon!' (very recent)",
          words: ["just"],
          a: "'I've just completed'",
          tip: "Just = very recently finished"
        },
        {
          type: "match",
          q: "Match time words:",
          pairs: [
            {left: "have been running for", right: "duration/period"},
            {left: "have been training since", right: "starting point"},
            {left: "have just finished", right: "very recent"},
            {left: "still haven't done", right: "expected but not yet"}
          ],
          a: "completed",
          tip: "For = period, since = starting point, modifiers = timing"
        }
      ]
    },

    // SET 4: Moving House Experience
    {
      id: "m8-review-s4",
      title: "Review 4: Moving to a New Home",
      category: "review",
      questions: [
        {
          type: "passage",
          passage: `**The Decision**\n\nWe've been living in our apartment for five years, but we've finally bought a house! We've just signed the contract this morning. We've saved money for this since we got married. We're incredibly excited but also nervous about the move!`
        },
        {
          type: "match",
          q: "Match the time expressions:",
          pairs: [
            {left: "for five years", right: "duration period"},
            {left: "since we married", right: "starting point"},
            {left: "just signed", right: "very recent"},
            {left: "finally bought", right: "completed at last"}
          ],
          a: "completed",
          tip: "For, since, and modifying adverbs"
        },
        {
          type: "input",
          q: `We've been living there ___ five years. (duration)`,
          a: "for",
          tip: "For + period of time"
        },
        {
          type: "passage",
          passage: `**Current Situation**\n\nRight now, we're packing boxes every evening. We've already packed the books and clothes. We still haven't packed the kitchen yet because we're using it. My wife has been labeling boxes to keep us organized. It's impossible to find anything right now - everything is so disorganized!`
        },
        {
          type: "mc",
          q: "Difference: 'have packed' vs 'haven't packed yet':",
          options: ["Packed = finished, haven't yet = still to do", "Both finished", "Both not done"],
          a: "Packed = finished, haven't yet = still to do",
          tip: "Already = done, yet = expected but not done"
        },
        {
          type: "order",
          q: "Negative prefixes: 'It's ___ to find' and 'Everything is ___'",
          words: ["impossible", "disorganized"],
          a: "'impossible' and 'disorganized'",
          tip: "Im- and dis- make words negative"
        },
        {
          type: "passage",
          passage: `**Renovation Plans**\n\nThe house needs work. We're going to paint all the rooms ourselves - we want to save money. I'm using online videos for learning how to tile the bathroom. We hope to finish the renovations before winter. We can't stand living in messy conditions!`
        },
        {
          type: "input",
          q: `We'll paint ___. (reflexive - we'll do it, not hire painters)`,
          a: "ourselves",
          tip: "Ourselves = we do the action ourselves"
        },
        {
          type: "match",
          q: "Match verb patterns:",
          pairs: [
            {left: "want to save", right: "infinitive"},
            {left: "hope to finish", right: "infinitive"},
            {left: "can't stand living", right: "gerund"},
            {left: "use for learning", right: "gerund for purpose"}
          ],
          a: "completed",
          tip: "Infinitives and gerunds after different verbs"
        },
        {
          type: "passage",
          passage: `**Moving Day Plans**\n\nMoving day is next Saturday! The moving company is arriving at 8am. We're meeting them at the old apartment. I'll make coffee for the movers when they arrive - that's a promise! My brother said he'll help us move the heavy furniture. We'll definitely be exhausted by evening!`
        },
        {
          type: "mc",
          q: "'The company is arriving' uses:",
          options: ["Present continuous for scheduled future", "Present simple", "Going to"],
          a: "Present continuous for scheduled future",
          tip: "Present continuous for definite arrangements"
        },
        {
          type: "input",
          q: `'I ___ make coffee for them' (spontaneous offer/promise)`,
          a: "'ll",
          tip: "Will for offers and promises"
        },
        {
          type: "passage",
          passage: `**Concerns and Possibilities**\n\nWe're a bit worried. The forecast says it might rain on moving day - that's possible but not certain. Some boxes might get wet if we're unlucky. The truck might not fit all our furniture. But I think we'll probably manage everything. We've been planning this carefully for months!`
        },
        {
          type: "order",
          q: "Certainty: 'It ___ rain' (50% possible) vs 'We'll ___ manage' (75% likely)",
          words: ["might", "probably"],
          a: "'might rain' vs 'probably manage'",
          tip: "Might = 50%, probably = 75%"
        },
        {
          type: "mc",
          q: "'Boxes might get wet' shows:",
          options: ["Future possibility - 50%", "Certain future", "Past event"],
          a: "Future possibility - 50%",
          tip: "Might = uncertain possibility"
        },
        {
          type: "passage",
          passage: `**First Night**\n\nWe've just moved in! The house looks empty but exciting. We've been unpacking for three hours straight. We still haven't found the bed sheets yet - they must be in one of these boxes! I'm absolutely exhausted. Tomorrow, we're definitely going to buy new curtains. This has been the most tiring but incredible day!`
        },
        {
          type: "input",
          q: `'We've ___ moved in!' (very recent action)`,
          a: "just",
          tip: "Just = very recently, short time ago"
        },
        {
          type: "match",
          q: "Match modal meanings:",
          pairs: [
            {left: "must be", right: "strong deduction 90%"},
            {left: "might be", right: "possibility 50%"},
            {left: "definitely going to", right: "certainty 100%"},
            {left: "probably will", right: "likelihood 75%"}
          ],
          a: "completed",
          tip: "Different certainty levels with modals"
        }
      ]
    },

    // SET 5: Organizing a Charity Dinner
    {
      id: "m8-review-s5",
      title: "Review 5: Planning a Big Event",
      category: "review",
      questions: [
        {
          type: "passage",
          passage: `**The Project Begins**\n\nOur community has decided to organize a charity dinner for the local hospital. We've been planning this event since September. I've volunteered myself to be the main organizer. We've already sold 150 tickets and we've just secured a great venue downtown yesterday!`
        },
        {
          type: "match",
          q: "Match present perfect uses:",
          pairs: [
            {left: "have been planning", right: "ongoing activity (PPC)"},
            {left: "have volunteered", right: "completed decision (PP)"},
            {left: "have sold", right: "achievement/result (PP)"},
            {left: "have just secured", right: "very recent action (PP)"}
          ],
          a: "completed",
          tip: "Present perfect continuous vs simple uses"
        },
        {
          type: "input",
          q: `I volunteered ___ to organize. (reflexive - I offered myself)`,
          a: "myself",
          tip: "Myself = I volunteered me, nobody else asked"
        },
        {
          type: "passage",
          passage: `**Menu Planning**\n\nWe want to serve delicious food that everyone will enjoy eating. The chef is using fresh ingredients for preparing all dishes. We need the menu to include vegetarian options. Some guests might have dietary restrictions - it's impossible to please everyone, but we'll try! Nothing will be frozen or unhealthy.`
        },
        {
          type: "order",
          q: "Purpose: 'Using fresh ingredients ___ preparing dishes'",
          words: ["for"],
          a: "'for preparing'",
          tip: "For + gerund shows purpose"
        },
        {
          type: "match",
          q: "Match verb patterns:",
          pairs: [
            {left: "enjoy eating", right: "gerund"},
            {left: "want to serve", right: "infinitive"},
            {left: "need to include", right: "infinitive"},
            {left: "use for preparing", right: "gerund for purpose"}
          ],
          a: "completed",
          tip: "Different verbs take different patterns"
        },
        {
          type: "passage",
          passage: `**Current Progress**\n\nHow long have you been working on decorations? someone asks me. For two weeks, I reply. We've decorated the main hall already, but we still haven't set up the tables yet. The weather forecast says it's going to be mild and clear that evening - perfect!`
        },
        {
          type: "mc",
          q: "'How long have you been working' asks about:",
          options: ["Duration of ongoing activity", "Finished action", "Future plan"],
          a: "Duration of ongoing activity",
          tip: "How long + PPC = duration question"
        },
        {
          type: "input",
          q: `We've decorated (finished), but we ___ haven't set up (not yet done).`,
          a: "still",
          tip: "Still = expected action continues to not happen"
        },
        {
          type: "passage",
          passage: `**The Event Schedule**\n\nThe dinner is this Saturday! Guests are arriving at 7pm. We're meeting caterers at 5pm for the final preparations. The band is performing at 8pm. I'm definitely going to give a speech about the charity. I'll probably feel nervous, but I'll do it anyway!`
        },
        {
          type: "order",
          q: "Future arrangements: 'Guests ___ arriving' and 'We're ___ caterers'",
          words: ["are", "meeting"],
          a: "'are arriving' and 'meeting'",
          tip: "Present continuous for scheduled future events"
        },
        {
          type: "match",
          q: "Match certainty levels:",
          pairs: [
            {left: "definitely going to", right: "100% decided"},
            {left: "probably will", right: "75% likely"},
            {left: "might", right: "50% possible"},
            {left: "is going to (forecast)", right: "evidence-based"}
          ],
          a: "completed",
          tip: "Different expressions of certainty"
        },
        {
          type: "passage",
          passage: `**Day-of Challenges**\n\nIt's Saturday morning! Some problems have appeared. The flowers haven't arrived yet - the delivery must have been delayed. Two volunteers have called in sick. I'll help set up tables myself if needed! One guest might not come because of the weather, but most will definitely attend.`
        },
        {
          type: "mc",
          q: "'Must have been delayed' shows:",
          options: ["Strong deduction about past (90%)", "Uncertain guess (50%)", "Definite past"],
          a: "Strong deduction about past (90%)",
          tip: "Must have = logical deduction from evidence"
        },
        {
          type: "input",
          q: `'I'll help ___' (reflexive - I'll do it personally)`,
          a: "myself",
          tip: "Myself = I personally will do it"
        },
        {
          type: "passage",
          passage: `**During the Event**\n\nIt's happening! Guests are enjoying themselves - everyone looks happy! The food tastes absolutely delicious - sweet desserts, savory mains, and fresh salads. I've just given my speech. My hands were shaking but I think I did well. People have been very generous with donations!`
        },
        {
          type: "order",
          q: "Reflexive: 'Guests are enjoying ___' (they're enjoying themselves/having fun)",
          words: ["themselves"],
          a: "'enjoying themselves'",
          tip: "Enjoy oneself = have a good time"
        },
        {
          type: "input",
          q: `'I've ___ given my speech' (very recent completion)`,
          a: "just",
          tip: "Just = moments ago, very recently"
        },
        {
          type: "passage",
          passage: `**Success!**\n\nThe evening has been incredible! We've raised over $10,000 for the hospital - that's more than we expected! This experience has taught me that organizing events is both challenging and unbelievably rewarding. I might volunteer to organize next year's event too. I know I'll definitely remember this night forever!`
        },
        {
          type: "mc",
          q: "'Unbelievably rewarding' - 'un-' here means:",
          options: ["Extremely (intensifier, not negative)", "Not believable", "Somewhat"],
          a: "Extremely (intensifier, not negative)",
          tip: "Un- can intensify, not always negative"
        },
        {
          type: "match",
          q: "Match the tense uses in this story:",
          pairs: [
            {left: "have raised", right: "completed achievement"},
            {left: "has taught", right: "experience/result"},
            {left: "might volunteer", right: "future possibility"},
            {left: "will remember", right: "certain prediction"}
          ],
          a: "completed",
          tip: "Different tenses serve different purposes",
          successTip: "MODULE 8 MASTERED! Present perfect (experiences, news, ongoing), modifying adverbs (just, already, still, yet), present perfect continuous (duration with for/since), verb patterns (gerunds/infinitives), reflexive pronouns (myself, ourselves, themselves), negative prefixes (un-, im-, dis-), articles (the vs zero), going to (plans/evidence), will (predictions/offers/promises), might (possibilities 50%), certainty levels (definitely 100%, probably 75%, might 50%). All patterns integrated in realistic contexts! 🏆📚✨"
        }
      ]
    }
  ]
};
